import { useState, useEffect } from "react";

const useRecording = (cameraStream) => {
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordedChunks, setRecordedChunks] = useState([]);
    const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    // Set up media recorder when camera stream is available
    useEffect(() => {
        if (cameraStream) {
            // Check if stream has audio tracks
            const audioTracks = cameraStream.getAudioTracks();
            const videoTracks = cameraStream.getVideoTracks();


            // Ensure audio tracks are enabled
            audioTracks.forEach(track => {
                track.enabled = true;
            });

            // Configure MediaRecorder with options to include audio
            let options = { mimeType: 'video/webm;codecs=vp9,opus' };

            // Fallback to other codecs if vp9,opus is not supported
            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                options = { mimeType: 'video/webm;codecs=vp8,opus' };
            }

            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                options = { mimeType: 'video/webm' };
            }


            const recorder = new MediaRecorder(cameraStream, options);
            setMediaRecorder(recorder);

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    setRecordedChunks(prev => [...prev, event.data]);
                }
            };

            recorder.onstop = () => {
                setRecordedChunks(currentChunks => {
                    if (currentChunks.length > 0) {
                        const blob = new Blob(currentChunks, { type: options.mimeType });
                        const url = URL.createObjectURL(blob);
                        setRecordedVideoUrl(url);
                    }
                    return currentChunks;
                });
            };
        }
    }, [cameraStream]);

    const startRecording = () => {
        if (mediaRecorder && cameraStream) {
            // Only reset chunks and URL if we're starting a completely fresh recording
            // (not continuing after timer extension or resuming from pause)
            if (!isRecording && !isPaused && recordedChunks.length === 0) {
                setRecordedChunks([]);
                setRecordedVideoUrl(null);
            }
            setIsRecording(true);
            setIsPaused(false);

            if (mediaRecorder.state === 'inactive') {
                mediaRecorder.start(1000); // Record in 1-second intervals
            }
        }
    };

    const pauseRecording = () => {
        setIsRecording(false);
        setIsPaused(true);

        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop(); // Stop instead of pause to trigger onstop event
        }
    };

    const resumeRecording = () => {
        setIsRecording(true);
        setIsPaused(false);

        if (mediaRecorder && cameraStream) {
            if (mediaRecorder.state === 'paused') {
                mediaRecorder.resume();
            } else if (mediaRecorder.state === 'inactive') {
                // If stopped, start recording again
                mediaRecorder.start(1000);
            }
        }
    };

    const continueRecording = () => {
        // This function is specifically for continuing recording after timer extension
        setIsRecording(true);
        setIsPaused(false);

        if (mediaRecorder && cameraStream && mediaRecorder.state === 'inactive') {
            mediaRecorder.start(1000);
        }
    };

    const stopRecording = () => {
        setIsRecording(false);
        setIsPaused(false);

        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
        }
    };

    const resetRecording = () => {
        setIsRecording(false);
        setIsPaused(false);
        setRecordedChunks([]);
        setRecordedVideoUrl(null);
    };

    return {
        mediaRecorder,
        recordedChunks,
        recordedVideoUrl,
        isRecording,
        isPaused,
        startRecording,
        pauseRecording,
        resumeRecording,
        continueRecording,
        stopRecording,
        setRecordedVideoUrl,
        resetRecording,
    };
};

export default useRecording;